import { Seance } from "domain/entities/seances.entities";
import {
  HiHeart,
  HiOutlineClock,
  HiOutlineDuplicate,
  HiOutlineFolderAdd,
  HiOutlineHeart,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineX,
} from "react-icons/hi";
import { BannerGrey300 } from "ui/assets/svg/seance-banners-desktop/BannerGrey300";
import { BannerLightGreen500 } from "ui/assets/svg/seance-banners-desktop/BannerLightGreen500";
import { BannerPaleGreen200 } from "ui/assets/svg/seance-banners-desktop/BannerPaleGreen200";
import { BannerPaleGreen400 } from "ui/assets/svg/seance-banners-desktop/BannerPaleGreen400";
import { BannerPink200 } from "ui/assets/svg/seance-banners-desktop/BannerPink200";
import { BannerPink300 } from "ui/assets/svg/seance-banners-desktop/BannerPink300";
import { BannerPink400 } from "ui/assets/svg/seance-banners-desktop/BannerPink400";
import { BannerPink500 } from "ui/assets/svg/seance-banners-desktop/BannerPink500";
import { CardBgGrey300 } from "ui/assets/svg/seance-card-backgrounds/CardBgGrey300";
import { CardBgLightGreen500 } from "ui/assets/svg/seance-card-backgrounds/CardBgLightGreen500";
import { CardBgPaleGreen200 } from "ui/assets/svg/seance-card-backgrounds/CardBgPaleGreen200";
import { CardBgPaleGreen400 } from "ui/assets/svg/seance-card-backgrounds/CardBgPaleGreen400";
import { CardBgPink200 } from "ui/assets/svg/seance-card-backgrounds/CardBgPink200";
import { CardBgPink300 } from "ui/assets/svg/seance-card-backgrounds/CardBgPink300";
import { CardBgPink400 } from "ui/assets/svg/seance-card-backgrounds/CardBgPink400";
import { CardBgPink500 } from "ui/assets/svg/seance-card-backgrounds/CardBgPink500";
import { useWindowWidth } from "ui/utils/hooks";
import { getTotalSeanceDuration } from "ui/utils/formatters";
import { useAppSelector } from "ui/hook/store";
import { selectIsPremium, selectUserName } from "domain/auth/auth.selectors";
import { PDFDownloadLink } from "@react-pdf/renderer";
import { SeancePDF } from "ui/pages/app/seances/seance/SeancePDF";
import {
  selectSeanceAdvice,
  selectSeanceMateriels,
} from "domain/seances/seances.selectors";
import { DownloadActivityTooltip } from "ui/pages/app/components/activities/DownloadActivityTooltip";
import { translate } from "ui/i18n";
import { CustomTextTooltip } from "ui/pages/app/components/CustomTextTooltip";
import { DownloadImageIcon } from "ui/assets/svg/icons/DownloadImageIcon";
import { DownloadActivityImagesTooltip } from "ui/pages/app/components/activities/techniques/DownloadActivityImagesTooltip";
import { PDFIcon } from "ui/assets/svg/icons/PDFIcon";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  useDisclosure,
} from "@chakra-ui/react";
import { useEffect, useRef } from "react";

export function SeanceBanner({
  seance,
  isFavorite,
  handleFavoriteClick,
  handleDuplication,
  openDeleteModal,
  handleEditSeance,
  handleDownloadClick,
  handleFolderClick,
  handleDownloadPDFClick,
  handleDownloadImagesClick,
  handleTimingClick,
  handleRemoveTimingClick,
  timingPopoverState,
  startTimes,
}: {
  seance: Seance;
  isFavorite?: boolean;
  handleFavoriteClick?: (e: any) => Promise<void>;
  handleDuplication: () => void;
  openDeleteModal: () => void;
  handleEditSeance: () => void;
  handleDownloadClick?: (e: any) => Promise<void>;
  handleFolderClick?: (e: any) => void;
  handleDownloadPDFClick?: (e: any) => void;
  handleDownloadImagesClick?: (e: any) => void;
  handleTimingClick?: (e: any) => void;
  handleRemoveTimingClick?: (e: any) => Promise<void>;
  timingPopoverState: ReturnType<typeof useDisclosure>;
  startTimes?: string[];
}) {
  const isDesktop = useWindowWidth();
  const isPremium = useAppSelector(selectIsPremium);
  const advice = useAppSelector(selectSeanceAdvice);
  const materiels = useAppSelector(selectSeanceMateriels);
  const userName = useAppSelector(selectUserName);
  const timingContentRef = useRef(null);

  useEffect(() => {
    if (timingPopoverState.isOpen && timingContentRef.current) {
      timingContentRef.current.focus();
    }
  }, [timingPopoverState.isOpen]);

  const handleOpenTimingClick = (e: any) => {
    if (timingPopoverState.isOpen) {
      timingPopoverState.onClose();
    } else {
      if (seance?.timing) {
        timingPopoverState.onOpen();
      } else {
        handleTimingClick && handleTimingClick(e);
      }
    }
  };

  const bannerInfos = getBannerImage(getTotalSeanceDuration(seance), isDesktop);
  return (
    <div className="activity-banner relative w-full">
      <bannerInfos.image width="100%" height="100%" />
      {isDesktop && (
        <div className="absolute right-6 top-6 hidden space-x-4 md:flex">
          {seance?.userId && (
            <CustomTextTooltip
              text={translate("ns1:Activities.Tooltips.AddToFolder")}
            >
              <button
                className="flex h-10 w-10 items-center justify-center rounded-xl bg-white"
                onClick={handleFolderClick}
              >
                <HiOutlineFolderAdd
                  className="text-secondaryGrey-900"
                  size={20}
                />
              </button>
            </CustomTextTooltip>
          )}
          <CustomTextTooltip
            text={
              isFavorite
                ? translate("ns1:Activities.Tooltips.RemoveFromFavorites")
                : translate("ns1:Activities.Tooltips.AddToFavorites")
            }
          >
            <button
              className="flex h-10 w-10 items-center justify-center rounded-xl bg-white"
              onClick={handleFavoriteClick}
            >
              {isFavorite ? (
                <HiHeart className="text-secondaryGrey-900" size={20} />
              ) : (
                <HiOutlineHeart className="text-secondaryGrey-900" size={20} />
              )}
            </button>
          </CustomTextTooltip>

          <Popover
            isOpen={timingPopoverState.isOpen}
            onOpen={seance?.timing ? timingPopoverState.onOpen : undefined}
            onClose={timingPopoverState.onClose}
            placement="bottom"
          >
            <PopoverTrigger>
              <CustomTextTooltip
                text={
                  seance.timing
                    ? translate("ns1:Activities.Tooltips.ManageTiming")
                    : translate("ns1:Activities.Tooltips.AddTiming")
                }
              >
                <button
                  className={`flex h-10 w-10 items-center justify-center rounded-xl ${
                    seance?.timing
                      ? timingPopoverState.isOpen
                        ? "bg-darkGrey-200"
                        : "bg-darkGrey-800"
                      : "bg-white"
                  }`}
                  onClick={handleOpenTimingClick}
                >
                  <HiOutlineClock
                    className={`${
                      seance?.timing && !timingPopoverState.isOpen
                        ? "text-white"
                        : "text-secondaryGrey-900"
                    }`}
                    size={20}
                  />
                </button>
              </CustomTextTooltip>
            </PopoverTrigger>

            {timingPopoverState.isOpen && (
              <PopoverContent
                style={{
                  isolation: "isolate",
                  zIndex: 100,
                  width: "fit-content",
                  border: "none",
                  backgroundColor: "transparent",
                  boxShadow: "none",
                }}
                ref={timingContentRef}
              >
                <div className="flex flex-col rounded-[20px] bg-white px-2 py-3 text-sm font-semibold text-darkGrey-700 shadow-lg">
                  <button
                    className="flex rounded-[10px] px-3 py-2 hover:bg-darkGrey-100"
                    onClick={handleTimingClick}
                  >
                    <HiOutlineClock
                      className="me-2 text-secondaryGrey-900"
                      size={20}
                    />
                    {translate("ns1:Seance.UpdateTimingStart")}
                  </button>
                  <button
                    className="flex rounded-[10px] px-3 py-2 hover:bg-darkGrey-100"
                    onClick={handleRemoveTimingClick}
                  >
                    <HiOutlineX
                      className="me-2 text-secondaryGrey-900"
                      size={20}
                    />
                    {translate("ns1:Seance.RemoveTiming")}
                  </button>
                </div>
              </PopoverContent>
            )}
          </Popover>

          {isPremium && (
            <CustomTextTooltip
              text={
                seance.isInniz
                  ? translate("ns1:Activities.Tooltips.Customize")
                  : translate("ns1:Activities.Tooltips.Modify")
              }
            >
              <button
                className="flex h-10 w-10 items-center justify-center rounded-xl bg-white"
                onClick={handleEditSeance}
              >
                <HiOutlinePencil className="text-secondaryGrey-900" size={20} />
              </button>
            </CustomTextTooltip>
          )}
          {seance?.userId && (
            <CustomTextTooltip
              text={translate("ns1:Activities.Tooltips.Duplicate")}
            >
              <button
                className="flex h-10 w-10 items-center justify-center rounded-xl bg-white"
                onClick={handleDuplication}
              >
                <HiOutlineDuplicate
                  className="text-secondaryGrey-900"
                  size={20}
                />
              </button>
            </CustomTextTooltip>
          )}
          {seance && (
            <DownloadActivityImagesTooltip activity="seance">
              <button
                className="flex h-10 w-10 items-center justify-center rounded-xl bg-white"
                onClick={handleDownloadImagesClick}
              >
                <DownloadImageIcon color="#2b3674" />
              </button>
            </DownloadActivityImagesTooltip>
          )}
          {isPremium ? (
            <PDFDownloadLink
              document={
                <SeancePDF
                  seance={seance}
                  advice={advice}
                  materiels={materiels}
                  userName={userName}
                  startTimes={startTimes}
                />
              }
              fileName={`${seance.title}.pdf`}
            >
              <DownloadActivityTooltip type="seance">
                <button
                  className="flex h-10 w-10 items-center justify-center rounded-xl bg-white"
                  onClick={handleDownloadPDFClick}
                >
                  <PDFIcon className="text-secondaryGrey-900" size={20} />
                </button>
              </DownloadActivityTooltip>
            </PDFDownloadLink>
          ) : (
            <DownloadActivityTooltip type="seance">
              <button
                className="flex h-10 w-10 items-center justify-center rounded-xl bg-white"
                onClick={(e) =>
                  handleDownloadClick ? handleDownloadClick(e) : null
                }
              >
                <PDFIcon className="text-secondaryGrey-900" size={20} />
              </button>
            </DownloadActivityTooltip>
          )}
          {seance?.userId && (
            <CustomTextTooltip
              text={translate("ns1:Activities.Tooltips.Delete")}
            >
              <button
                className="flex h-10 w-10 items-center justify-center rounded-xl bg-white"
                onClick={openDeleteModal}
              >
                <HiOutlineTrash className="text-secondaryGrey-900" size={20} />
              </button>
            </CustomTextTooltip>
          )}
        </div>
      )}
      <div
        className={`items-bottom absolute bottom-6 flex w-full space-x-6 px-6 text-2xl ${bannerInfos.color}`}
      >
        <span className="font-bold">{seance.title}</span>
        {/* <span className="flex items-end">
          <HiThumbUp size={28} className="me-2" />
          <span className=" font-medium">12</span>
        </span> */}
      </div>
    </div>
  );
}

function getBannerImage(duration: number, isDesktop: boolean) {
  if (duration <= 70)
    return {
      image: isDesktop ? BannerGrey300 : CardBgGrey300,
      color: "text-white",
    };
  else if (duration <= 90)
    return {
      image: isDesktop ? BannerPaleGreen200 : CardBgPaleGreen200,
      color: "text-secondaryGrey-900",
    };
  else if (duration <= 120)
    return {
      image: isDesktop ? BannerLightGreen500 : CardBgLightGreen500,
      color: "text-secondaryGrey-900",
    };
  else if (duration <= 150)
    return {
      image: isDesktop ? BannerPaleGreen400 : CardBgPaleGreen400,
      color: "text-white",
    };
  else if (duration <= 180)
    return {
      image: isDesktop ? BannerPink200 : CardBgPink200,
      color: "text-secondaryGrey-900",
    };
  else if (duration <= 210)
    return {
      image: isDesktop ? BannerPink300 : CardBgPink300,
      color: "text-secondaryGrey-900",
    };
  else if (duration <= 360)
    return {
      image: isDesktop ? BannerPink400 : CardBgPink400,
      color: "text-white",
    };
  else
    return {
      image: isDesktop ? BannerPink500 : CardBgPink500,
      color: "text-white",
    };
}
